// @ts-nocheck -- 开发期模拟数据不参与生产 API 契约校验
/**
 * 订单域 Mock（订单 / 物流 / 退款 / 导出）
 */

import type { MockAdapter } from 'axios-mock-adapter';
import { AfterSaleStatus, OrderStatus, RefundStatus } from '@/shared';
import { type LogisticsTrack, type Order } from '@gyjk/shared';

const traceId = () => `mock-${Date.now()}`;
const ok = (data: unknown) => [
  200,
  { success: true, code: 'OK', message: 'ok', data, traceId: traceId() },
];
const fail = (message: string) => [
  400,
  { success: false, code: 'BAD_REQUEST', message, data: null, traceId: traceId() },
];

const statusNames: Record<string, string> = {
  [OrderStatus.PENDING_PAYMENT]: '未支付',
  [OrderStatus.PAID]: '待发货',
  [OrderStatus.SHIPPED]: '已发货',
  [OrderStatus.COMPLETED]: '已完成',
  [OrderStatus.CANCELLED]: '已关闭',
  [OrderStatus.FULL_REFUNDED]: '已退款',
};

function createOrder(index: number, status: OrderStatus, afterSale: AfterSaleStatus): Order {
  return {
    id: `order-${index + 1}`,
    orderNo: `ORD20260627${String(index + 1).padStart(4, '0')}`,
    memberId: `m-${index + 1}`,
    status,
    items: [
      {
        id: `oi-${index + 1}`,
        orderId: `order-${index + 1}`,
        skuId: 'sku-1',
        productId: 'p-1',
        productName: '复合维生素片 60粒',
        productImage: 'https://via.placeholder.com/80x80?text=VIT',
        skuSpec: '60粒/瓶',
        price: 1990,
        quantity: 2,
        subtotal: 3980,
      },
    ],
    totalAmount: 3980,
    discountAmount: 0,
    shippingFee: 0,
    finalAmount: 3980,
    receiverName: `用户 ${index + 1}`,
    receiverPhone: '138****0000',
    receiverAddress: '上海市浦东新区测试路 123 号',
    remark: '',
    afterSaleStatus: afterSale,
    createdAt: '2026-06-27 10:00:00',
    updatedAt: '2026-06-27 10:00:00',
  };
}

const orders: Order[] = [
  createOrder(0, OrderStatus.PENDING_PAYMENT, AfterSaleStatus.NONE),
  createOrder(1, OrderStatus.PAID, AfterSaleStatus.NONE),
  createOrder(2, OrderStatus.SHIPPED, AfterSaleStatus.NONE),
  createOrder(3, OrderStatus.COMPLETED, AfterSaleStatus.NONE),
  createOrder(4, OrderStatus.PAID, AfterSaleStatus.REQUESTED),
  createOrder(5, OrderStatus.SHIPPED, AfterSaleStatus.REVIEWING),
];

const logisticsMap = new Map<string, LogisticsTrack>();

const refunds = [
  {
    id: 'ref-1',
    orderId: 'order-5',
    orderNo: 'ORD202606270005',
    memberId: 'm-5',
    amount: 3980,
    actualAmount: 0,
    reason: '买错了',
    type: 'REFUND',
    status: RefundStatus.PENDING,
    images: [],
    createdAt: '2026-06-28T10:00:00.000Z',
  },
  {
    id: 'ref-2',
    orderId: 'order-6',
    orderNo: 'ORD202606270006',
    memberId: 'm-6',
    amount: 1990,
    actualAmount: 0,
    reason: '商品破损',
    type: 'RETURN',
    status: RefundStatus.PENDING,
    images: [],
    createdAt: '2026-06-28T11:00:00.000Z',
  },
];

function paginate<T>(list: T[], page: number, pageSize: number) {
  const start = (page - 1) * pageSize;
  return list.slice(start, start + pageSize);
}

function findOrder(id: string): Order | undefined {
  return orders.find((o) => o.id === id);
}

export function setup(mock: MockAdapter): void {
  // ───── 订单列表 / 详情 ─────
  mock.onGet('/admin/orders').reply((config) => {
    const params = config.params || {};
    const page = Number(params.page) || 1;
    const pageSize = Number(params.pageSize) || 20;
    const status = params.status;
    const afterSaleStatus = params.afterSaleStatus;
    const keyword = (params.keyword || '').toLowerCase();
    let list = orders;
    if (status) list = list.filter((o) => o.status === status);
    if (afterSaleStatus) list = list.filter((o) => o.afterSaleStatus === afterSaleStatus);
    if (keyword) {
      list = list.filter(
        (o) =>
          o.orderNo.toLowerCase().includes(keyword) ||
          o.receiverName.toLowerCase().includes(keyword) ||
          o.items.some((i) => i.productName.toLowerCase().includes(keyword)),
      );
    }
    return ok({
      list: paginate(list, page, pageSize).map((o) => ({
        ...o,
        statusName: statusNames[o.status],
      })),
      total: list.length,
    });
  });

  mock.onGet(/\/admin\/orders\/([^/]+)/).reply((config) => {
    const id = (config.url as string).split('/').pop()!;
    const order = findOrder(id);
    return order ? ok({ ...order, statusName: statusNames[order.status] }) : fail('订单不存在');
  });

  mock.onPost(/\/admin\/orders\/([^/]+)\/ship/).reply((config) => {
    const id = (config.url as string).split('/')[3];
    const order = findOrder(id);
    if (!order) return fail('订单不存在');
    const body = JSON.parse(config.data || '{}');
    order.status = OrderStatus.SHIPPED;
    order.logisticsNo = body.logisticsNo;
    order.logisticsCompany = body.logisticsCompany;
    order.shippedAt = new Date().toISOString();
    order.updatedAt = new Date().toISOString();
    return ok(order);
  });

  mock.onPut(/\/admin\/orders\/([^/]+)\/status/).reply((config) => {
    const id = (config.url as string).split('/')[3];
    const order = findOrder(id);
    if (!order) return fail('订单不存在');
    const body = JSON.parse(config.data || '{}');
    order.status = body.status;
    order.updatedAt = new Date().toISOString();
    if (body.afterSaleStatus) order.afterSaleStatus = body.afterSaleStatus;
    return ok(order);
  });

  mock.onPost('/admin/orders/export').reply((config) => {
    const body = JSON.parse(config.data || '{}');
    const ids = body.ids as string[] | undefined;
    const list = ids ? orders.filter((o) => ids.includes(o.id)) : orders;
    return ok({
      url: 'https://example.com/mock/export/orders.xlsx',
      count: list.length,
    });
  });

  // ───── 物流 ─────
  mock.onGet(/\/orders\/([^/]+)\/logistics/).reply((config) => {
    const id = (config.url as string).split('/')[2];
    const order = findOrder(id);
    if (!order) return fail('订单不存在');
    const track = logisticsMap.get(id);
    if (track) return ok(track);
    return ok({
      orderId: id,
      logisticsNo: order.logisticsNo || '',
      logisticsCompany: order.logisticsCompany || '',
      status: order.status === OrderStatus.SHIPPED ? 'SHIPPED' : 'PENDING',
      tracks: [
        { time: order.createdAt, status: '已下单', description: '订单创建成功', location: '' },
        { time: order.shippedAt || order.createdAt, status: '已发货', description: '商家已发货', location: '上海' },
      ],
      fallback: true,
      fallbackMessage: '第三方物流 API 未配置，返回模拟轨迹',
    });
  });

  mock.onPost(/\/admin\/orders\/([^/]+)\/logistics/).reply((config) => {
    const id = (config.url as string).split('/')[3];
    const order = findOrder(id);
    if (!order) return fail('订单不存在');
    const body = JSON.parse(config.data || '{}') as LogisticsTrack;
    const track: LogisticsTrack = {
      orderId: id,
      logisticsNo: body.logisticsNo,
      logisticsCompany: body.logisticsCompany,
      status: body.status || 'SHIPPED',
      tracks: body.tracks || [],
      fallback: body.logisticsNo?.startsWith('SIM') ?? false,
      fallbackMessage: body.logisticsNo?.startsWith('SIM') ? '模拟物流单号，API 占位' : undefined,
    };
    logisticsMap.set(id, track);
    order.logisticsNo = body.logisticsNo;
    order.logisticsCompany = body.logisticsCompany;
    order.updatedAt = new Date().toISOString();
    return ok(track);
  });

  // ───── 退款 ─────
  mock.onGet('/admin/refunds').reply((config) => {
    const params = config.params || {};
    const page = Number(params.page) || 1;
    const pageSize = Number(params.pageSize) || 20;
    const status = params.status;
    const orderNo = params.orderNo;
    let list = refunds;
    if (status) list = list.filter((r) => r.status === status);
    if (orderNo) list = list.filter((r) => r.orderNo.includes(orderNo));
    return ok({ list: paginate(list, page, pageSize), total: list.length });
  });

  mock.onPost(/\/admin\/refunds\/([^/]+)\/audit/).reply((config) => {
    const id = (config.url as string).split('/')[3];
    const refund = refunds.find((r) => r.id === id);
    if (!refund) return fail('退款不存在');
    const body = JSON.parse(config.data || '{}');
    if (body.approved) {
      refund.status = RefundStatus.PROCESSING;
      refund.actualAmount = body.actualAmount ?? refund.amount;
      const order = findOrder(refund.orderId);
      if (order) {
        if (refund.actualAmount >= order.finalAmount) {
          order.status = OrderStatus.FULL_REFUNDED;
        }
        order.afterSaleStatus = AfterSaleStatus.COMPLETED;
        order.updatedAt = new Date().toISOString();
      }
    } else {
      refund.status = RefundStatus.REJECTED;
      const order = findOrder(refund.orderId);
      if (order) {
        order.afterSaleStatus = AfterSaleStatus.REJECTED;
        order.updatedAt = new Date().toISOString();
      }
    }
    return ok(refund);
  });
}
// @ts-nocheck -- 开发期模拟数据不参与生产 API 契约校验
